// Action Type
import {
  SET_DIRECTION,
  SET_CURRENT_PATH
} from 'store/reducers/navigation/actionTypes';
import { SET_IS_DEEPLINKING } from 'store/reducers/deeplink/actionTypes';
import {
  GTM_USER_EVENT,
  GTM_USER_EVENT_CART,
  GTM_USER_SIDEBAR_BUTTON,
  GTM_USER_EVENT_TELL_US_MORE,
  GTM_USER_EVENT_DEFAULT_DATE,
  GTM_USER_EVENT_ADD_PACKAGE,
  GTM_IS_IDK,
  GTM_USER_EVENT_MILEAGE_ENTERED,
  GTM_USER_EVENT_SIGN_ENTERED,
  GTM_USER_EVENT_NOTES_ENTERED,
  GTM_USER_EVENT_SIGNATURE_FULL_SCREEN,
  GTM_USER_EVENT_OPEN_DISCLAIMER_MODAL,
  GTM_SET_DIMENSION_CONFIRMATION_KEY,
  GTM_GENERIC_EVENT,
  GTM_SET_DIMENSION
} from 'store/reducers/gtm/actionTypes';
import { SET_QUERY_PARAMS } from 'store/reducers/queryParams/actionTypes';
import { SET_APPOINTMENT_FILTER_DAYS_BY } from 'store/reducers/userInteractions/actionTypes';

// Actions
import { gtmUserEvent, gtmStartGenericEvent } from 'store/reducers/gtm/actions';
import { setCurrentPath } from 'store/reducers/navigation/actions';

// Selectors
import {
  currentPath as getCurrentPath,
  previousPath as getPreviousPath
} from 'store/reducers/navigation/selectors';
import {
  getStartTime,
  getServices,
  isIDK
} from 'store/reducers/appointment/selectors';
import {
  getGtmFirstAvailableTimeslot,
  getNoFindsAlertShown,
  isModifyEnabled
} from 'store/reducers/userInteractions/selectors';
import { getPolicyProperty } from 'store/reducers/dealerSettings/selectors';
import {
  getDeeplinkKind,
  getIsDeeplinking
} from 'store/reducers/deeplink/selectors';
import { getIsDealerOpenNow } from 'store/reducers/selfCheckIn/selectors';

import { NAVIGATION } from 'constants/navigations';

// date-fns
import isSameDay from 'date-fns/is_same_day';

// Utils
import { toCapitalize, isEmpty } from 'utils/string';

// Constants
import {
  HOME_ROUTE,
  SIGN_IN_ROUTE,
  PAGE_NAME_BY_ROUTE,
  VEHICLE_ROUTE,
  RECALL_SERVICES_ROUTE
} from 'constants/routes';
import { WHOAMI as CP8_APP_NAME } from 'constants/appConfig';
import {
  SCI_PAGE_NAME_BY_ROUTE,
  APP_NAME as SCI_APP_NAME,
  SCI_SIGNATURE_ROUTE,
  SCI_EDIT_SERVICES_ROUTE,
  SCI_SERVICES_ROUTE
} from 'constants/selfCheckIn';

const { BACKWARD } = NAVIGATION;

/**
 * Add user event for GTM when *dataLayers* is defined
 * @param eventElement should describe what a user interacted with
 * @param eventAction What the user did. ( Clicked, dragged )
 * @param eventResult What happened for success ( and failure if possible )
 * @param eventLocation Describe user interactions
 * @param eventValue The value ( generally undefined )
 */
function pushUserEvent(
  eventElement,
  eventAction,
  eventResult,
  eventLocation,
  eventValue
) {
  window.dataLayer.push({
    event: 'userEvent',
    eventElement: formatGtmString(eventElement),
    eventAction: formatGtmString(eventAction),
    eventResult: formatGtmString(eventResult),
    eventLocation: formatGtmString(eventLocation),
    eventValue: formatGtmString(eventValue)
  });
}

function pushGenericEvent(eventCategory, eventAction, eventLabel, eventValue) {
  const appName = window.dataLayer[0].appName;
  window.dataLayer.push({
    event: 'genericEvent',
    eventCategory: `${appName}: ${formatGtmString(eventCategory)}`,
    eventAction: formatGtmString(eventAction),
    eventLabel: formatGtmString(eventLabel),
    eventValue: formatGtmString(eventValue)
  });
}

// @TODO: refactor to use pushSetDimension method only
function pushSetDimensionConfirmationKey(confirmationKey) {
  window.dataLayer.push({
    confirmationKey
  });
}

/*
 *  @param dimension Object
 */
function pushSetDimension(dimension) {
  window.dataLayer.push(dimension);
}

function pushUserEventPayload(payload) {
  pushUserEvent(
    payload.eventElement,
    payload.eventAction,
    payload.eventResult,
    payload.eventLocation,
    payload.eventValue
  );
}

function pushGenericEventPayload(payload) {
  pushGenericEvent(
    payload.eventCategory,
    payload.eventAction,
    payload.eventLabel,
    payload.eventValue
  );
}

function getPageName(route: string) {
  switch (window.dataLayer[0].appName) {
    case CP8_APP_NAME:
      return PAGE_NAME_BY_ROUTE[route];

    case SCI_APP_NAME:
      return SCI_PAGE_NAME_BY_ROUTE[route];

    default:
      return '';
  }
}

function formatGtmString(gtmString: string) {
  let temp = gtmString || '';
  if (temp.indexOf('GTM_CURRENT_PAGE') >= 0) {
    const currentPath = getCurrentPath(store.getState());
    temp = temp.replace('GTM_CURRENT_PAGE', getPageName(currentPath));
  }
  if (temp.indexOf('GTM_PREVIOUS_PAGE') >= 0) {
    const previousPath = getPreviousPath(store.getState());
    temp = temp.replace('GTM_PREVIOUS_PAGE', getPageName(previousPath));
  }
  return toCapitalize(temp);
}

/**
 * Add user event for GTM when *dataLayers* is defined.
 * @param pageUrl should describe the url of current page
 * @param pageTitle should describe page title.
 */
function pushTrackPageview(pageUrl, pageTitle) {
  window.dataLayer.push({
    event: 'trackPageview',
    pageUrl,
    pageTitle: pageTitle || pageUrl
  });
}

function isDeepLink(deeplinkKind) {
  switch (deeplinkKind) {
    case 'readApptFind':
      return 'Navigation through Unauthenticated Appt Review';
    default:
      return;
  }
}

function setEventAction(deepLinkKind, modifyEnabled) {
  if (isEmpty(deepLinkKind)) {
    if (modifyEnabled) {
      return 'Navigation to modify appointment';
    } else {
      return 'Navigation';
    }
  } else {
    return isDeepLink(deepLinkKind);
  }
}

export const gtmMiddleware = store => next => async action => {
  switch (action.type) {
    case SET_DIRECTION:
      {
        const currentPath = getCurrentPath(store.getState());
        const vehicleBuilderAlert = getNoFindsAlertShown(store.getState());
        const deepLinkKind = getDeeplinkKind(store.getState());
        const modifyEnabled = isModifyEnabled(store.getState());
        if (BACKWARD === action.payload) {
          if (currentPath !== RECALL_SERVICES_ROUTE) {
            await store.dispatch(
              gtmUserEvent(
                `On Back Button${
                  vehicleBuilderAlert ? '- no results FIND ME' : ''
                }`,
                setEventAction(deepLinkKind, modifyEnabled),
                'Go Back',
                `GTM_CURRENT_PAGE > Header`
              )
            );
          }
        } else if (
          currentPath !== HOME_ROUTE &&
          currentPath !== SIGN_IN_ROUTE &&
          currentPath !== VEHICLE_ROUTE &&
          currentPath !== SCI_SIGNATURE_ROUTE &&
          (currentPath !== SCI_EDIT_SERVICES_ROUTE ||
            !getIsDealerOpenNow(store.getState()))
        ) {
          if (currentPath === SCI_SERVICES_ROUTE && !action.payload) {
            await store.dispatch(
              gtmUserEvent(
                'Edit Services',
                'Clicked',
                'Edit Services',
                `GTM_CURRENT_PAGE > Header`
              )
            );
          } else {
            await store.dispatch(
              gtmUserEvent(
                'On Next Button',
                'Navigation',
                'Go Next',
                `GTM_CURRENT_PAGE > Footer`
              )
            );
          }
        }
      }
      break;
    case SET_CURRENT_PATH:
      {
        const pageTitle = getPageName(action.payload);
        if (
          pageTitle &&
          (window.dataLayer[0].appName === CP8_APP_NAME ||
            window.dataLayer[0].appName === SCI_APP_NAME)
        ) {
          pushTrackPageview(action.payload, pageTitle);
          await pushUserEvent(
            pageTitle,
            'View',
            `${pageTitle} Viewed`,
            pageTitle
          );
        }
      }
      break;
    case SET_QUERY_PARAMS:
      if (window.dataLayer[0].appName !== SCI_APP_NAME) {
        store.dispatch(setCurrentPath('/'));
      }
      break;
    case GTM_USER_EVENT:
      pushUserEventPayload(action.payload);
      break;
    case GTM_GENERIC_EVENT:
      pushGenericEventPayload(action.payload);
      break;
    case GTM_USER_EVENT_CART:
      {
        const payload = {
          ...action.payload,
          eventLocation: 'GTM_CURRENT_PAGE > Cart Section'
        };
        pushUserEventPayload(payload);
      }
      break;
    case GTM_USER_SIDEBAR_BUTTON:
      {
        const payload = {
          ...action.payload,
          eventLocation: 'GTM_CURRENT_PAGE > Settings Menu'
        };
        pushUserEventPayload(payload);
      }
      break;
    case GTM_USER_EVENT_TELL_US_MORE:
      {
        const getTellusmoreSvcId = getPolicyProperty(store.getState());
        const tellusmoreSvcId = getTellusmoreSvcId('tellusmoreSvcId');
        const services = getServices(store.getState());
        const findCallback = ({ id }) => id === tellusmoreSvcId;
        const tellusmoreSvc = services.find(findCallback);
        if (tellusmoreSvc && tellusmoreSvc.notes) {
          const payload = {
            ...action.payload,
            eventValue: tellusmoreSvc.notes
          };
          pushUserEventPayload(payload);
        }
      }
      break;
    case GTM_USER_EVENT_DEFAULT_DATE:
      {
        const startTime = getStartTime(store.getState());
        const firstAvailableTimeSlot = getGtmFirstAvailableTimeslot(
          store.getState()
        );
        if (isSameDay(startTime, firstAvailableTimeSlot)) {
          pushUserEventPayload(action.payload);
        }
      }
      break;
    case SET_APPOINTMENT_FILTER_DAYS_BY:
      if (action.payload) {
        pushUserEvent(
          'Morning/Afternoon Filter',
          'Selected',
          `Filter '${action.payload}' Selected`,
          'GTM_CURRENT_PAGE > Filter Days By morning/afternoon Section',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_ADD_PACKAGE:
      if (action.payload) {
        pushUserEvent(
          'Package',
          'Selected',
          'Package Added',
          'GTM_CURRENT_PAGE > Maintenance Package > Package',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_MILEAGE_ENTERED:
      if (action.payload) {
        pushUserEvent(
          'Mileage Input',
          'Enter mileage',
          'Mileage Filled',
          'Vehicle Page > Mileage section',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_SIGN_ENTERED:
      if (action.payload) {
        pushUserEvent(
          'Sign Input',
          'Enter Sign',
          'Sign Saved',
          'Signature Page > Signature section',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_NOTES_ENTERED:
      if (action.payload) {
        pushUserEvent(
          'Appointment notes Input',
          'Enter notes',
          'Notes Saved',
          'Signature Page > Signature section',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_SIGNATURE_FULL_SCREEN:
      if (action.payload) {
        pushUserEvent(
          'drawing-component__button-right',
          'Click button',
          'Signature modal opened',
          'Signature Page > Signature section',
          action.payload
        );
      }
      break;
    case GTM_USER_EVENT_OPEN_DISCLAIMER_MODAL:
      if (action.payload) {
        pushUserEvent(
          'disclaimers link',
          'Click link',
          'Disclaimer modal opened',
          'Signature Page > Signature section',
          action.payload
        );
      }
      break;
    case GTM_IS_IDK:
      if (action.payload && isIDK(store.getState())) {
        pushUserEvent(
          `On ${action.payload} button`,
          'navigation',
          `${action.payload} with step(s) remaining`,
          action.payload.toUpperCase() === 'CONTINUE'
            ? 'Vehicle Page > Services Page'
            : 'Vehicle Page > Mileage section'
        );
      }
      break;
    case GTM_SET_DIMENSION_CONFIRMATION_KEY:
      pushSetDimensionConfirmationKey(action.payload);
      break;
    case GTM_SET_DIMENSION:
      pushSetDimension(action.payload);
      break;
    case SET_IS_DEEPLINKING:
      {
        const isDeeplinking = getIsDeeplinking(store.getState());
        if (isDeeplinking && !action.payload) {
          store.dispatch(gtmStartGenericEvent());
        }
      }
      break;
    default:
  }
  next(action);
};



// WEBPACK FOOTER //
// ./src/store/middlewares/gtm-middleware.js